'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { useId, useState } from 'react';
import { useDictionary, useLocale, useSiteSettings } from '@/components/providers';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import { CONSENT_CATEGORIES } from './consent.constants';
import type { ServiceConsentState } from './consent.types';
import {
    isCategorySelected,
    isServiceSelected,
    selectCategory,
    selectService,
    servicesInCategory,
} from './consent-services';
import { useConsent } from './ConsentProvider';
import { ConsentCategoryItem } from './ConsentCategoryItem';

const PRIMARY_BTN = 'btn btn-primary';
const OUTLINE_BTN = 'btn btn-outline';
/** Mirrors the footer navigation links. */
const TEXT_BTN = 'cursor-pointer text-sm text-copy-muted transition-colors hover:text-title-2';

export function CookieBanner() {
    const { isBannerVisible, view, setView, hasDecision, undecidedServices, closeBanner, acceptAll, rejectAll } =
        useConsent();
    const { privacySettings } = useSiteSettings();
    const locale = useLocale();
    const { a11y, consent } = useDictionary();
    const banner = consent.banner;
    const privacyPageSlug = privacySettings?.privacyPage?.slug;
    // The privacy page is referenced from the same-language siteSettingsLang, so its
    // own language wins over the request locale (see resolveHref).
    const privacyLocale = privacySettings?.privacyPage?.language ?? locale;
    const headingId = useId();

    if (!isBannerVisible) return null;

    // Services added after an earlier decision: say so instead of repeating the
    // first-visit copy, and keep the existing selection pre-filled underneath.
    const isUpdate = hasDecision && undecidedServices.length > 0;
    const compactTitle = isUpdate ? banner.updateTitle : banner.title;
    const compactDescription = isUpdate ? banner.updateDescription : banner.description;

    const privacyLink = (
        <>
            {' '}
            {banner.privacyPrefix}{' '}
            {privacyPageSlug ? (
                <Link
                    href={`/${privacyLocale}/m/${privacyPageSlug}`}
                    className="underline underline-offset-2 transition-colors hover:text-primary"
                >
                    {banner.privacyLinkText}
                </Link>
            ) : (
                banner.privacyLinkText
            )}
            .
        </>
    );

    return (
        <section
            role="dialog"
            aria-modal="false"
            aria-labelledby={headingId}
            aria-label={a11y.privacyNotice}
            className="fixed inset-x-0 bottom-0 z-40"
        >
            <div className="pointer-events-none p-3 sm:p-4">
                <div
                    className={cn(
                        'pointer-events-auto relative mx-auto flex flex-col overflow-y-auto max-w-7xl',
                        'max-h-[calc(100dvh-5rem)] text-copy-2',
                        'rounded-2xl border border-(--pg-overlay-border) bg-(--pg-overlay)',
                        'p-5 shadow-(--pg-overlay-shadow) sm:p-6',
                    )}
                >
                    {hasDecision && (
                        <button
                            type="button"
                            onClick={closeBanner}
                            aria-label={banner.close}
                            className="absolute end-3 top-3 inline-flex size-8 cursor-pointer items-center justify-center rounded-lg text-copy-muted transition-colors hover:bg-surface-3 hover:text-title"
                        >
                            <UiIcon name="x-mark" className="size-3" />
                        </button>
                    )}

                    {view === 'compact' ? (
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
                            <div className="space-y-1.5">
                                <h2 id={headingId} className="text-base font-medium text-title">
                                    {compactTitle}
                                </h2>
                                <p className="text-sm text-copy-2">
                                    {compactDescription}
                                    {privacyLink}
                                </p>
                            </div>

                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:flex-nowrap self-end">
                                <button
                                    type="button"
                                    onClick={() => setView('settings')}
                                    className={cn(TEXT_BTN, 'sm:me-2 my-3 sm:my-0')}
                                >
                                    {banner.configure}
                                </button>
                                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                                    {banner.rejectAll}
                                </button>
                                <button type="button" onClick={acceptAll} className={PRIMARY_BTN}>
                                    {banner.acceptAll}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <ConsentSettingsPanel headingId={headingId} privacyLink={privacyLink} />
                    )}
                </div>
            </div>
        </section>
    );
}

function ConsentSettingsPanel({ headingId, privacyLink }: { headingId: string; privacyLink: ReactNode }) {
    const { serviceConsent, services, save, acceptAll, rejectAll, setView } = useConsent();
    const { consent } = useDictionary();
    const banner = consent.banner;
    // Initialized once on mount; the panel remounts when the view toggles, so it
    // always picks up the current consent without a synchronizing effect. New
    // services simply have no entry yet and therefore start switched off.
    const [selection, setSelection] = useState<ServiceConsentState>(() => serviceConsent);

    // Only list categories the catalog actually covers. Required categories
    // (necessary cookies) always stay visible as a disclosure.
    const visibleCategories = CONSENT_CATEGORIES.filter(
        (category) => category.required || services.some((service) => service.category === category.key),
    );

    return (
        <div className="flex flex-col gap-4">
            {/* Back to the initial compact banner with the three buttons. */}
            <button
                type="button"
                onClick={() => setView('compact')}
                className="-ml-0.5 inline-flex shrink-0 cursor-pointer items-center gap-1.5 self-start text-sm text-copy-muted transition-colors hover:text-title"
            >
                <UiIcon name="arrow-left" className="size-4" aria-hidden="true" />
                {banner.back}
            </button>

            {/* Grouping only — the whole card scrolls (overflow-y-auto on the card) once it hits the viewport cap. */}
            <div className="space-y-4">
                <div className="space-y-1.5 pe-8">
                    <h2 id={headingId} className="text-base font-medium text-title">
                        {banner.settingsTitle}
                    </h2>
                    <p className="text-sm text-copy-2">
                        {banner.settingsDescription}
                        {privacyLink}
                    </p>
                </div>

                <ul className="-my-1 divide-y divide-hairline">
                    {visibleCategories.map((category) => {
                        const categoryServices = servicesInCategory(services, category.key);

                        return (
                            <ConsentCategoryItem
                                key={category.key}
                                category={category}
                                services={categoryServices}
                                checked={isCategorySelected(selection, category.key, categoryServices)}
                                isServiceChecked={(service) => isServiceSelected(selection, service)}
                                onCategoryChange={(checked) =>
                                    setSelection((prev) => selectCategory(prev, categoryServices, checked))
                                }
                                onServiceChange={(service, checked) =>
                                    setSelection((prev) => selectService(prev, service, checked))
                                }
                            />
                        );
                    })}
                </ul>
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                    {banner.rejectAll}
                </button>
                <button type="button" onClick={acceptAll} className={OUTLINE_BTN}>
                    {banner.acceptAll}
                </button>
                <button type="button" onClick={() => save(selection)} className={PRIMARY_BTN}>
                    {banner.save}
                </button>
            </div>
        </div>
    );
}
